import PropTypes from "prop-types";
import { cloneElement, Component } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { Manager, Reference, Popper } from "react-popper";

import { domId } from "common/utils";

import "./hovercard.scss";

export class Hovercard extends Component {
    static defaultProps = {
        displayTimeout: 100,
        position: "top",
        triggerEvent: "hover",
        visible: true,
        header: "",
        body: ""
    };
    
    static propTypes = {
        displayTimeout: PropTypes.number,
        position: PropTypes.oneOf([
            "bottom",
            "top",
            "left",
            "right",
            "bottom-start",
            "bottom-end",
            "top-start",
            "top-end",
            "left-start",
            "left-end",
            "right-start",
            "right-end",
            "auto"
        ]),
        header: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
        body: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
        triggerEvent: PropTypes.oneOf(["click", "hover"]),
        visible: PropTypes.bool
    };
    
    static getDerivedStateFromProps(props, state) {
        return {
            visible: props.visible && state.visible
        };
    }
    
    constructor(props) {
        super(props);
        
        let portal = document.getElementById("hovercard-portal");
        if (!portal) {
            portal = document.createElement("div");
            portal.setAttribute("id", "hovercard-portal");
            document.body.appendChild(portal);
        }
        
        this.portalEl = portal;
        this.tooltipId = domId("hovercard-");
        
        this.state = {
            visible: false
        };
    }

    componentDidUpdate() {
        this.boundClickCallback = this.boundClickCallback || this.handleToggleOff.bind(this);
        if (this.state.visible) {
            document.addEventListener("click", this.boundClickCallback);
        } else {
            document.removeEventListener("click", this.boundClickCallback);
        }
    }
    
    componentWillUnmount() {
        document.removeEventListener("click", this.boundClickCallback);
    }

    handleToggleOn = () => this.toggleHovercard(true);

    handleToggleOff = () => this.toggleHovercard(false);
    
    toggleHovercard = (visible) => {
        // Don't toggle hovercard if both of these are null
        if (!this.props.header && !this.props.body) {
            return;
        }
        if (this.hoverWait) {
            clearTimeout(this.hoverWait);
        }
        
        this.hoverWait = setTimeout(() => this.setState({ visible }), this.props.displayTimeout);
    };
    
    makeHoverProps() {
        if (this.props.show) {
            return {};
        }

        if (this.props.triggerEvent === "click") {
            return {
                onClick: this.toggleHovercard
            };
        }
        
        return {
            onMouseEnter: this.handleToggleOn,
            onMouseLeave: this.handleToggleOff
        };
    }
    
    renderTrigger(children, ref) {
        const propList = {
            "aria-describedby": this.tooltipId,
            ...this.makeHoverProps()
        };
        
        if (children.type instanceof Function) {
            return (
                <div {...propList} ref={ref}>
                    {children}
                </div>
            );
        }
    
        // Basic DOM nodes can be cloned and have more props applied.
        return cloneElement(children, {
            ...propList,
            ref
        });
    }
    
    render() {
        const modifiers = [
            {
                name: "hide",
                enabled: false
            },
            {
                name: "preventOverflow",
                options: {
                    padding: 10,
                    rootBoundary: "viewport" // TODO: do we need it here. viewport is default value
                }
            }
        ];
        
        const visible = this.props?.show || this.state.visible;
        
        let hoverCard = null;
        if (visible) {
            hoverCard = ReactDOM.createPortal(
                <Popper placement={this.props.position} modifiers={modifiers}>
                    {({ ref, style, placement, arrowProps }) => (
                        <div
                            ref={ref}
                            style={style}
                            className={classNames("hovercard", this.props.className, `placement-${placement}`, { visible })}
                            {...this.makeHoverProps()}
                        >
                            {this.props.header && <div className="hover-header">{this.props.header}</div>}
                            {this.props.body && <div className={classNames("hover-body", this.props.bodyClassName)}>{this.props.body}</div>}
                            <span
                                className={`hovercard-arrow placement-${placement}`}
                                ref={arrowProps.ref}
                                style={arrowProps.style}
                            />
                        </div>
                    )}
                </Popper>,
                this.portalEl
            );
        }
        
        return (
            <Manager>
                <Reference>{({ ref }) => this.renderTrigger(this.props.children, ref)}</Reference>
                {hoverCard}
            </Manager>
        );
    }
}
